import React from 'react';
import BaseEChart from './BaseEChart';
import {
  CHART_AXIS_LABEL_STYLE,
  CHART_COLORS,
  CHART_FONT_FAMILY,
  CONSENSUS_PALETTE,
} from './chartConstants';
import { PLACEHOLDER } from '@utils/format';
import type { ChartOption } from '@app-types/chart';
import type { ConsensusTrendPoint } from '@app-types/performance';
import {
  adjustConsensusTrendValue,
  computeConsensusTrendAxis,
  getRawConsensusTrendValue,
  type AdjustedBarValue,
  type ConsensusTrendKey,
} from './consensusTrendAxis';

interface AxisTooltipParam {
  seriesName?: string;
  data?: AdjustedBarValue;
  color?: string;
  axisValueLabel?: string;
  axisValue?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });

export interface ConsensusTrendChartProps {
  data: ConsensusTrendPoint[];
  height?: number;
}

const SERIES: Array<{
  key: ConsensusTrendKey;
  name: string;
  color: string;
}> = [
  { key: 'bear', name: '弱気', color: CONSENSUS_PALETTE[4] },
  { key: 'slightBear', name: 'やや弱気', color: CONSENSUS_PALETTE[3] },
  { key: 'neutral', name: '中立', color: CONSENSUS_PALETTE[2] },
  { key: 'slightBull', name: 'やや強気', color: CONSENSUS_PALETTE[1] },
  { key: 'bull', name: '強気', color: CONSENSUS_PALETTE[0] },
];

const ConsensusTrendChart: React.FC<ConsensusTrendChartProps> = ({
  data,
  height = 171,
}) => {
  const yAxis = computeConsensusTrendAxis(data);

  const option: ChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      appendToBody: true,
      className: 'esmart-chart-tooltip esmart-chart-tooltip--consensus-trend',
      formatter: (params: unknown) => {
        const items = Array.isArray(params)
          ? (params as AxisTooltipParam[])
          : [params as AxisTooltipParam];
        if (items.length === 0) {
          return '';
        }
        const periodLabel = items[0]?.axisValueLabel ?? items[0]?.axisValue ?? '';
        // Show buckets top-to-bottom (strong bullish first) using raw counts,
        // not the display-adjusted bar values.
        const rows = [...items]
          .reverse()
          .map((item) => {
            const rawValue = getRawConsensusTrendValue(item.data);
            const displayValue = rawValue !== null ? String(rawValue) : PLACEHOLDER;
            const marker = item.color
              ? `<span class="esmart-chart-tooltip__marker" style="background:${escapeHtml(
                  item.color,
                )}"></span>`
              : '';
            return `<div class="esmart-chart-tooltip__row">${marker}<span class="esmart-chart-tooltip__name">${escapeHtml(
              item.seriesName ?? '',
            )}</span><span class="esmart-chart-tooltip__value">${displayValue}</span></div>`;
          })
          .join('');
        const title = periodLabel
          ? `<div class="esmart-chart-tooltip__title">${escapeHtml(periodLabel)}</div>`
          : '';
        return `${title}${rows}`;
      },
    },
    grid: {
      left: 28,
      right: 2,
      top: 14,
      bottom: 22,
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: data.map((point) => point.label),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        interval: 0,
        ...(CHART_AXIS_LABEL_STYLE as any),
        lineHeight: 17,
        align: 'center',
        margin: 8,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: yAxis.max,
      interval: yAxis.interval,
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: {
        show: true,
        lineStyle: { color: CHART_COLORS.grid, width: 1 },
      },
      axisLabel: {
        ...(CHART_AXIS_LABEL_STYLE as any),
        lineHeight: 14,
        margin: 8,
        formatter: (value: number) => String(value),
      },
    },
    series: SERIES.map((seriesItem) => ({
      name: seriesItem.name,
      type: 'bar' as const,
      stack: 'consensus',
      barWidth: 36,
      data: data.map((point) => adjustConsensusTrendValue(point[seriesItem.key])),
      itemStyle: { color: seriesItem.color },
      label: {
        show: true,
        position: 'inside' as const,
        color: '#ffffff',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: CHART_FONT_FAMILY,
        letterSpacing: -0.4,
        formatter: (params: any) => {
          const rawValue = getRawConsensusTrendValue(params.data);
          return rawValue !== null && rawValue > 0 ? String(rawValue) : '';
        },
      },
      emphasis: { disabled: true },
    })),
  };

  return (
    <BaseEChart
      option={option}
      height={height}
      className="consensus-trend-chart"
      ariaLabel="コンセンサス推移チャート"
      animate
    />
  );
};

export default ConsensusTrendChart;
